import i18n from 'i18n-js';

import {
    clamp,
    EditorArea,
    nearestPointOnAreaOutline,
    OutlinePoint,
    Point,
    SizeMeters,
    snapPointMeters,
} from './mapGeometry';

export type MapOpeningKind = 'Entrance' | 'Exit';

/** `position` is normalized 0..1 against the map's global rectangle. */
export interface MapOpening {
    id: string;
    kind: MapOpeningKind;
    label: string | null;
    position: Point;
}

/** TablesMapManager.MaxMapOpenings / MaxMapOpeningLabelLength. */
export const MAX_OPENINGS = 20;
export const OPENING_LABEL_MAX_LENGTH = 50;
export const OPENING_MIN_GAP_METERS = 1;
const ON_OUTLINE_TOLERANCE_METERS = 0.05;
/** The marker's bar along the wall, in SVG units: drawn at this size whatever the map scale. */
export const OPENING_BAR_LENGTH = 36;
export const OPENING_BAR_WIDTH = 8;

function distance(a: Point, b: Point): number {
    return Math.hypot(a.x - b.x, a.y - b.y);
}

export function openingOutlinePoint(
    opening: MapOpening,
    size: SizeMeters,
    area: EditorArea
): OutlinePoint {
    const metres = { x: opening.position.x * size.width, y: opening.position.y * size.height };
    return nearestPointOnAreaOutline(metres, area);
}

export function openingPositionAt(p: Point, size: SizeMeters, area: EditorArea): Point {
    const snapped = snapPointMeters(nearestPointOnAreaOutline(p, area).point);
    return { x: clamp(snapped.x / size.width, 0, 1), y: clamp(snapped.y / size.height, 0, 1) };
}

export function openingsTooClose(
    openings: MapOpening[],
    id: string,
    size: SizeMeters,
    area: EditorArea
): MapOpening[] {
    const target = openings.find((o) => o.id === id);
    if (!target) return [];
    const point = openingOutlinePoint(target, size, area).point;
    return openings.filter(
        (o) =>
            o.id !== id &&
            distance(openingOutlinePoint(o, size, area).point, point) < OPENING_MIN_GAP_METERS
    );
}

export function splitOpeningsByOutline(
    openings: MapOpening[],
    size: SizeMeters,
    fromArea: EditorArea,
    toArea: EditorArea
): { kept: MapOpening[]; removed: MapOpening[] } {
    const kept: MapOpening[] = [];
    const removed: MapOpening[] = [];
    for (const opening of openings) {
        const current = openingOutlinePoint(opening, size, fromArea).point;
        const onNewOutline = nearestPointOnAreaOutline(current, toArea).point;
        if (distance(current, onNewOutline) > ON_OUTLINE_TOLERANCE_METERS) {
            removed.push(opening);
        } else {
            kept.push({ ...opening, position: openingPositionAt(current, size, toArea) });
        }
    }
    return { kept, removed };
}

export function openingName(openings: MapOpening[], opening: MapOpening): string {
    const label = opening.label?.trim();
    if (label) return label;
    const key =
        opening.kind === 'Entrance'
            ? 'tableMaps.wizard.features.entranceN'
            : 'tableMaps.wizard.features.exitN';
    const n =
        openings.filter((o) => o.kind === opening.kind).findIndex((o) => o.id === opening.id) + 1;
    return i18n.t(key, { n });
}

export function openingListName(openings: MapOpening[], opening: MapOpening): string {
    const generic = openingName(openings, { ...opening, label: null });
    const label = opening.label?.trim();
    return label ? `${generic} · ${label}` : generic;
}
